import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import { SITE } from "@/lib/site";

const EFFECTIVE_DATE = "18 June 2025";

const SECTIONS = [
  {
    id: "about",
    title: "1. About these terms",
    body: [
      `These Terms of Service ("Terms") govern your use of the website nexaverve.uk and any services provided by NexaVerve Solutions ("we", "us", "our"), a facilities and compliance consultancy based in Manchester, United Kingdom.`,
      `By using this website or engaging our services, you agree to these Terms. If you do not agree, please do not use the site or our services.`,
    ],
  },
  {
    id: "services",
    title: "2. Services we offer",
    body: [
      `We provide consultancy and delivery services including cleaning, small maintenance, PAT testing, compliance support, health & safety documentation and operational support. The exact scope of any engagement will be agreed in writing (email, quote, proposal or contract) before work begins.`,
      `Our public-facing information (including this website and Instagram) is for general marketing purposes and does not constitute a binding offer.`,
    ],
  },
  {
    id: "quotes",
    title: "3. Quotes, orders and pricing",
    list: [
      "All prices are quoted in GBP and are, unless stated otherwise, exclusive of VAT.",
      "Quotes are typically valid for 30 days from the date of issue.",
      "A quote is an estimate based on the information you provide. Actual price may change if the site conditions, scope or timing differ from what was described.",
      "A booking becomes binding once you confirm in writing (email, WhatsApp or signed quote) and, where applicable, once any agreed deposit is received.",
    ],
  },
  {
    id: "payment",
    title: "4. Payment terms",
    list: [
      "Unless agreed otherwise, invoices are payable within 14 days of the invoice date.",
      "For one-off jobs (e.g. end-of-tenancy or after-builders cleans), we may require full payment on completion or a deposit before the work.",
      "Late payments may attract statutory interest and reasonable recovery costs under the Late Payment of Commercial Debts (Interest) Act 1998.",
      "We accept bank transfer as the standard method of payment. Card or other payment methods may be offered at our discretion.",
    ],
  },
  {
    id: "cancellation",
    title: "5. Cancellations and rescheduling",
    body: [
      `We understand plans change. However, our teams and subcontractors reserve time for your booking, so please give us as much notice as possible.`,
    ],
    list: [
      "More than 48 hours' notice: no cancellation fee.",
      "24–48 hours' notice: a fee of up to 50% of the quoted price may apply.",
      "Less than 24 hours' notice or no-show on the day: a fee of up to 100% of the quoted price may apply.",
      "Rescheduling requests are welcome and, where possible, we will move your slot without charge.",
    ],
  },
  {
    id: "access",
    title: "6. Access, utilities and your responsibilities",
    body: ["To deliver services safely and effectively, you agree to:"],
    list: [
      "Provide safe, unobstructed access to the site at the agreed time.",
      "Ensure water, electricity and lighting are available where the job requires them.",
      "Make us aware of any known hazards, restrictions, alarms, pets or vulnerable persons on site.",
      "Secure or move away valuables, fragile items and confidential documents before work begins.",
      "Ensure you have the right to instruct work at the property (e.g. as owner, landlord, agent or authorised representative).",
    ],
    body_after: [
      "We reserve the right to refuse or stop work if the site is unsafe, if access is not provided, or if the scope has materially changed.",
    ],
  },
  {
    id: "standards",
    title: "7. Our standards",
    body: [
      `We aim to deliver every job to a high, professional standard. If you're not happy with anything, please tell us within 48 hours of completion and we will return to rectify any reasonable snags free of charge, subject to access and safety.`,
      `We do not accept liability for pre-existing damage, worn or aged surfaces, staining that cannot be removed by industry-standard methods, or issues introduced by third parties after we leave site.`,
    ],
  },
  {
    id: "liability",
    title: "8. Liability",
    body: [
      `We carry public liability insurance of £5,000,000 and, where relevant, employer's liability insurance as required by UK law. Certificates are available on request.`,
      `Nothing in these Terms limits our liability for death or personal injury caused by our negligence, for fraud, or for any other liability that cannot be excluded by law.`,
      `Subject to that, our total liability arising out of any single engagement is limited to the total fees paid or payable to us for that engagement in the 12 months preceding the claim.`,
      `We are not liable for indirect or consequential losses, loss of profits, loss of business or loss of data.`,
    ],
  },
  {
    id: "subcontractors",
    title: "9. Subcontractors",
    body: [
      `We may engage vetted subcontractors to deliver part of a service. We remain your point of contact and remain responsible for the standard of the work delivered under our name.`,
    ],
  },
  {
    id: "compliance",
    title: "10. Compliance & regulatory work",
    body: [
      `PAT testing, risk assessments, method statements and other compliance documentation are provided in good faith and based on the information available to us at the time of inspection. They are a snapshot at that point and do not remove your obligation to keep your premises and equipment safe and compliant on an ongoing basis.`,
      `We recommend re-testing and reviews at the intervals stated on our documentation or as required by your insurer / regulator.`,
    ],
  },
  {
    id: "ip",
    title: "11. Intellectual property",
    body: [
      `All content on this website — including text, graphics, logos, the NexaVerve name and mark — is owned by NexaVerve Solutions or its licensors and protected by UK and international copyright and trademark law. You may not reproduce or use any part of it for commercial purposes without our prior written consent.`,
    ],
  },
  {
    id: "privacy",
    title: "12. Privacy",
    body: [
      `Our handling of personal data is described in our Privacy Policy. By using the site or engaging our services you acknowledge that policy.`,
    ],
  },
  {
    id: "termination",
    title: "13. Termination",
    body: [
      `Either party may terminate an engagement at any time by giving written notice. If we terminate for cause (for example, non-payment or unsafe site conditions), fees for work completed up to the date of termination remain payable in full.`,
    ],
  },
  {
    id: "law",
    title: "14. Governing law",
    body: [
      `These Terms and any dispute or claim arising out of them are governed by the laws of England and Wales, and the courts of England and Wales have exclusive jurisdiction.`,
    ],
  },
  {
    id: "changes",
    title: "15. Changes to these terms",
    body: [
      `We may update these Terms from time to time. The effective date at the top shows the latest version. Continued use of the site or our services after an update means you accept the revised Terms.`,
    ],
  },
  {
    id: "contact",
    title: "16. Contact us",
    body: ["If you have any questions about these Terms, please contact us:"],
    list: [
      `Email: ${SITE.email}`,
      `WhatsApp: ${SITE.whatsappPretty}`,
      `Location: ${SITE.location}`,
    ],
  },
];

export default function TermsOfService() {
  useEffect(() => {
    document.title = `Terms of Service · ${SITE.name}`;
    window.scrollTo(0, 0);
  }, []);

  return (
    <main data-testid="terms-page" className="relative bg-[#0A0A0A] text-white min-h-screen">
      <Navbar />

      <section className="pt-32 sm:pt-40 pb-16">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <Link
            to="/"
            data-testid="terms-back-home"
            className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-neutral-500 hover:text-[#FBBF24] transition-colors mb-8"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Back home
          </Link>

          <p className="tag-label mb-5">// Legal / Terms</p>
          <h1
            data-testid="terms-heading"
            className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05] text-white"
          >
            Terms of <span className="text-[#FBBF24]">Service.</span>
          </h1>
          <p className="mt-6 font-mono text-xs uppercase tracking-[0.25em] text-neutral-500">
            Effective {EFFECTIVE_DATE}
          </p>

          <p className="mt-10 text-neutral-300 leading-relaxed">
            These terms set out how{" "}
            <span className="text-white">{SITE.name}</span> works with clients
            for consultancy, cleaning, maintenance, PAT testing and compliance
            services across Manchester and the North West.
          </p>
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <div className="space-y-10">
            {SECTIONS.map((s) => (
              <div key={s.id} id={s.id} data-testid={`terms-section-${s.id}`}>
                <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-white mb-4">
                  {s.title}
                </h2>
                {(s.body || []).map((p, i) => (
                  <p
                    key={i}
                    className="text-neutral-300 leading-relaxed mb-4 last:mb-0"
                  >
                    {p}
                  </p>
                ))}
                {s.list && s.list.length > 0 && (
                  <ul className="mt-2 space-y-2 list-none">
                    {s.list.map((item, i) => (
                      <li
                        key={i}
                        className="flex items-start gap-3 text-neutral-300 leading-relaxed"
                      >
                        <span className="mt-2 w-1.5 h-1.5 bg-[#FBBF24] shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {(s.body_after || []).map((p, i) => (
                  <p
                    key={`ba-${i}`}
                    className="text-neutral-300 leading-relaxed mt-4"
                  >
                    {p}
                  </p>
                ))}
              </div>
            ))}
          </div>

          <div className="mt-16 pt-10 border-t border-white/10 flex flex-wrap items-center justify-between gap-4">
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-neutral-600">
              © {new Date().getFullYear()} {SITE.name} · {SITE.domain}
            </p>
            <Link
              to="/privacy"
              data-testid="terms-privacy-link"
              className="font-mono text-[11px] uppercase tracking-[0.25em] text-neutral-500 hover:text-[#FBBF24] transition-colors"
            >
              Read our Privacy Policy →
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
